import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

// Prefer CRA-style env if it exists at build time; otherwise choose by hostname (sandbox vs main).
const API_BASE =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL && String(process.env.REACT_APP_BACKEND_URL)) ||
  ((typeof window !== 'undefined' && String(window.location.hostname).indexOf('ai-translation-frontend-sandbox') !== -1)
    ? 'https://ai-translation-backend-sandbox.onrender.com'
    : 'https://ai-translation-backend.onrender.com');

export default function PaymentTracking() {
  const { jwt } = useAuth();
  const [older, setOlder] = useState(15);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState([]);
  const [message, setMessage] = useState("");
  const [reconcilingAll, setReconcilingAll] = useState(false);


  const formatStatusLabel = (s) => {
    try {
      const v = String(s || "");
      if (!v) return "";
      return v
        .split("_")
        .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : ""))
        .join(" ");
    } catch {
      return String(s || "");
    }
  };

  const headers = jwt ? { Authorization: `Bearer ${jwt}` } : {};

  const providerLabel = (p) => {
    const v = String(p || '').toLowerCase();
    if (v === 'stripe_project') return 'Custom Translation (Stripe)';
    if (v === 'paypal_project') return 'Custom Translation (PayPal)';
    if (v === 'stripe') return 'Credits (Stripe)';
    if (v === 'paypal') return 'Credits (PayPal)';
    return String(p || '');
  };


  const fetchPending = async () => {
    setLoading(true);
    setMessage("");
    try {
      const res = await axios.get(`${API_BASE}/admin/payments/pending`, {
        params: { older_than_minutes: older },
        headers,
      });
      setPending(res.data || []);
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Failed to load pending payments.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPending();
    // eslint-disable-next-line
  }, []);

  const reconcileOne = async (provider, external_id) => {
    try {
      const res = await axios.post(
        `${API_BASE}/admin/payments/reconcile`,
        { provider, external_id },
        { headers }
      );
      setPending((rows) =>
        rows.map((r) =>
          r.external_id === external_id && r.provider === provider
            ? { ...r, status: res.data?.result === "credited" ? "succeeded" : r.status, _lastResult: res.data }
            : r
        )
      );
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Failed to reconcile.");
    }
  };

  const reconcileAll = async () => {
    setReconcilingAll(true);
    setMessage("");
    try {
      const res = await axios.post(
        `${API_BASE}/admin/payments/reconcile-all`,
        {},
        { params: { older_than_minutes: older }, headers }
      );
      setMessage(
        `Reconcile All: credited=${res.data?.credited}, already=${res.data?.already}, pending=${res.data?.pending}, failed=${res.data?.failed}, skipped=${res.data?.skipped}, error=${res.data?.error}`
      );
      await fetchPending();
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Failed to reconcile all.");
    } finally {
      setReconcilingAll(false);
    }
  };

  return (
    <div data-testid="payment-tracking-page" className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Clickable logo header */}
      <a
        href="/"
        className="flex items-center justify-center gap-3 pt-6 cursor-pointer select-none"
      >
        <img
          src="/logo.png"
          alt="Zab AI Logo"
          className="h-15 w-20"
          style={{ paddingTop: 12 }}
        />
        <h1 className="text-3xl font-bold">Zab AI Translation System™</h1>
      </a>

      {/* Back to Admin Panel */}
      <div>
        <a href="/admin" className="bg-gray-300 px-3 py-2 rounded hover:bg-gray-400 inline-block">
          {"< Back to Admin Panel"}
        </a>
      </div>

      <h2 data-testid="payment-tracking-title" className="text-2xl font-bold">Payment Tracking</h2>
      <p className="text-sm text-gray-600">
        View and reconcile pending payment transactions older than N minutes. Only Admins/Super Admins can access.
      </p>

      <div className="flex items-end gap-3">
        <div>
          <label className="block text-sm font-medium">Older than (minutes)</label>
          <input
            type="number"
            min="0"
            step="1"
            value={older}
            onChange={(e) => setOlder(parseInt(e.target.value || "0", 10))}
            className="border rounded p-2 w-40"
          />
        </div>
        <button
          data-testid="payment-tracking-refresh"
          onClick={fetchPending}
          disabled={loading}
          className={`text-white px-4 py-2 rounded ${loading ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`}
          style={{ backgroundColor: "#00a7e1" }}
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
        <button
          data-testid="payment-tracking-reconcile-all"
          onClick={reconcileAll}
          disabled={reconcilingAll || pending.length === 0}
          className={`text-white px-4 py-2 rounded ${reconcilingAll || pending.length === 0 ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`}
          style={{ backgroundColor: "#94c83d" }}
        >
          {reconcilingAll ? "Reconciling..." : "Reconcile All"}
        </button>
      </div>

      {message && <div className="text-sm">{message}</div>}

      <div className="overflow-x-auto">
        <table data-testid="payment-tracking-table" className="min-w-full border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-2 border-b">Created</th>
              <th className="text-left p-2 border-b">Provider</th>
              <th className="text-left p-2 border-b">External ID</th>
              <th className="text-left p-2 border-b">User</th>
              <th className="text-right p-2 border-b">USD</th>
              <th className="text-right p-2 border-b">Credits</th>
              <th className="text-left p-2 border-b">Status</th>
              <th className="text-left p-2 border-b">Action</th>
            </tr>
          </thead>
          <tbody>
            {pending.length === 0 ? (
              <tr>
                <td data-testid="payment-tracking-empty" colSpan="8" className="p-4 text-center text-gray-500">
                  {loading ? "Loading..." : "No pending transactions."}
                </td>
              </tr>
            ) : (
              pending.map((row) => (
                <tr key={`${row.provider}:${row.external_id}`} className="border-b">
                  <td className="p-2">{new Date(row.created_at).toLocaleString()}</td>
                  <td className="p-2">{providerLabel(row.provider)}</td>
                  <td className="p-2 font-mono text-xs break-all">{row.external_id}</td>
                  <td className="p-2">{row.user_email || row.user_id}</td>
                  <td className="p-2 text-right">${(row.usd ?? 0).toFixed(2)}</td>
                  <td className="p-2 text-right">{(row.credits ?? 0).toFixed(2)}</td>
                  <td className="p-2">{formatStatusLabel(row.status)}</td>
                  <td className="p-2">
                    <button
                      onClick={() => reconcileOne(row.provider, row.external_id)}
                      className="text-white px-3 py-1 rounded hover:opacity-90"
                      style={{ backgroundColor: "#00a7e1" }}
                    >
                      Reconcile
                    </button>
                    {row._lastResult && (
                      <div className="text-xs text-gray-600 mt-1">
                        result: <b>{row._lastResult.result}</b>{row._lastResult.reason ? ` — ${row._lastResult.reason}` : ""}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
